import { Button, Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import { AiOutlineClose, AiOutlineCopy, AiOutlineFileText } from 'react-icons/ai';
import './DuplicateLayerModal.scss';

export type DuplicateMode = 'copy' | 'scratch';

interface DuplicateLayerModalProps {
  open: boolean;
  layerName?: string;
  loading?: boolean;
  onClose: () => void;
  onCreate: (mode: DuplicateMode) => void;
}

// US-101365: presents the two duplication strategies (preserve symbology
// vs. start from defaults) before firing the duplicate mutation. The
// caller stays in charge of running the mutation so this stays a dumb
// presentational component.
const DuplicateLayerModal: React.FC<DuplicateLayerModalProps> = ({ open, loading, onClose, onCreate }) => {
  const [mode, setMode] = useState<DuplicateMode>('copy');

  useEffect(() => {
    if (open) setMode('copy');
  }, [open]);

  const description =
    mode === 'copy' ? (
      <>
        <strong>Copy Layer Properties:</strong> Creates a duplicate with all symbology, formatting, labels, and styling preserved from the original
        layer.
      </>
    ) : (
      <>
        <strong>Start from Scratch:</strong> Creates a duplicate with default symbology and formatting.
      </>
    );

  return (
    <Modal
      className="duplicate-layer-modal"
      open={open}
      onCancel={onClose}
      centered
      maskClosable={false}
      closable={false}
      footer={null}
      width={460}
      destroyOnClose
    >
      <div className="duplicate-layer-modal__header">
        <h3 className="duplicate-layer-modal__title">Duplicate Layer</h3>
        <button className="duplicate-layer-modal__close" onClick={onClose} aria-label="Close" disabled={loading}>
          <AiOutlineClose />
        </button>
      </div>
      <div className="duplicate-layer-modal__body">
        <div className="duplicate-layer-modal__cards">
          <button
            type="button"
            className={`duplicate-layer-modal__card${mode === 'copy' ? ' duplicate-layer-modal__card--active' : ''}`}
            onClick={() => setMode('copy')}
          >
            <span className="duplicate-layer-modal__card-icon">
              <AiOutlineCopy />
            </span>
            <span className="duplicate-layer-modal__card-label">Copy Layer Properties</span>
          </button>
          <button
            type="button"
            className={`duplicate-layer-modal__card${mode === 'scratch' ? ' duplicate-layer-modal__card--active' : ''}`}
            onClick={() => setMode('scratch')}
          >
            <span className="duplicate-layer-modal__card-icon">
              <AiOutlineFileText />
            </span>
            <span className="duplicate-layer-modal__card-label">Start from Scratch</span>
          </button>
        </div>

        <p className="duplicate-layer-modal__description">{description}</p>

        <div className="duplicate-layer-modal__actions">
          <Button className="duplicate-layer-modal__create-btn" loading={loading} onClick={() => onCreate(mode)}>
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DuplicateLayerModal;
